import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Category, Product } from "./catalog.types";

function mapProduct(row: any): Product {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    category_slug: row.category_slug,
    short: row.short,
    description: row.description,
    long_description: row.long_description,
    image: row.image ?? "",
    gallery: row.gallery ?? [],
    video_url: row.video_url ?? null,
    specs: Array.isArray(row.specs) ? row.specs : [],
    features: row.features ?? [],
    includes: row.includes ?? [],
    best_for: row.best_for ?? [],
    faq: Array.isArray(row.faq) ? row.faq : [],
    synonyms: row.synonyms ?? [],
    dimensions: row.dimensions ?? null,
    power: row.power ?? null,
    weight: row.weight ?? null,
    meta_title: row.meta_title ?? null,
    meta_description: row.meta_description ?? null,
    popular: !!row.popular,
    brandable: !!row.brandable,
    sort_order: row.sort_order ?? 0,
    is_published: !!row.is_published,
  };
}

function mapCategory(row: any): Category {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    description: row.description,
    sort_order: row.sort_order ?? 0,
    is_published: !!row.is_published,
  };
}

export const listPublishedCategories = createServerFn({ method: "GET" }).handler(
  async () => {
    const { data, error } = await supabaseAdmin
      .from("categories")
      .select("*")
      .eq("is_published", true)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapCategory);
  },
);

export const listPublishedProducts = createServerFn({ method: "GET" }).handler(
  async () => {
    const { data, error } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("is_published", true)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapProduct);
  },
);

export const getProductBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row ? mapProduct(row) : null;
  });

export const getCategoryBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("categories")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const { data: products, error: pErr } = await supabaseAdmin
      .from("products")
      .select("*")
      .eq("category_slug", data.slug)
      .eq("is_published", true)
      .order("sort_order", { ascending: true });
    if (pErr) throw new Error(pErr.message);
    return {
      category: mapCategory(row),
      products: (products ?? []).map(mapProduct),
    };
  });
